/**
 * Sidebar Menu Catalog
 *
 * 사이드바가 표시할 수 있는 모든 메뉴 항목의 단일 소스. 백엔드는 `menu_key`만
 * 알면 되고, 라벨/아이콘/경로/권한 정보는 이 카탈로그가 책임진다.
 *
 * - 1depth (`parentKey === null`)와 2depth로 구성
 * - `menu_key`는 `<parent>.<child>` 형태의 dotted notation (예: `status.my-work`)
 * - 워크스페이스별 `menu_settings`와 머지할 때 이 카탈로그가 화이트리스트 역할
 */

/** 1depth menu keys */
export type ParentMenuKey = 'status' | 'project' | 'datacenter'

/** All menu keys (1depth + 2depth) */
export type MenuKey =
  // 1depth
  | ParentMenuKey
  // status 하위
  | 'status.my-work'
  | 'status.overall'
  | 'status.detail'
  | 'status.performance'
  | 'status.report-weekly'
  | 'status.report-monthly'
  | 'status.strategy'
  // project 하위
  | 'project.task'
  | 'project.performance'
  | 'project.report'
  | 'project.meeting'
  // datacenter 하위
  | 'datacenter.staff-workload'
  | 'datacenter.change-history'
  | 'datacenter.university'
  | 'datacenter.export'

/** 1depth 아이콘 식별자 — Sidebar에서 실제 lucide 컴포넌트로 매핑 */
export type MenuIconName = 'Monitor' | 'Clipboard' | 'Database'

export interface MenuCatalogEntry {
  /** 백엔드 menu_key와 1:1 매칭 */
  key: MenuKey
  /** 사이드바에 표시되는 라벨 (한글) */
  label: string
  /** 1depth면 null, 2depth면 부모 키 */
  parentKey: ParentMenuKey | null
  /** menu_settings에 없을 때 사용할 기본 정렬 순서 (같은 parent 안에서) */
  defaultSortOrder: number
  /** menu_settings에 없을 때 사용할 기본 활성 여부 */
  defaultActive: boolean
  /** 1depth 항목 아이콘 (2depth는 점/원으로 통일되어 있어 불필요) */
  iconName?: MenuIconName
  /** 2depth 라우트 경로 */
  path?: string
  /** owner/admin만 표시되는 항목 (백엔드 is_active와 무관하게 권한 가드 우선) */
  adminOnly?: boolean
}

/**
 * 사이드바 메뉴 카탈로그. 새 메뉴 추가는 여기서 한다.
 *
 * `defaultSortOrder`는 1depth는 1depth끼리, 같은 parent의 2depth는 그 그룹 안에서
 * 0부터 카운트한다.
 */
export const MENU_CATALOG: readonly MenuCatalogEntry[] = [
  // ===== 1depth =====
  {
    key: 'status',
    label: '현황',
    parentKey: null,
    defaultSortOrder: 0,
    defaultActive: true,
    iconName: 'Monitor',
  },
  {
    key: 'project',
    label: '프로젝트',
    parentKey: null,
    defaultSortOrder: 1,
    defaultActive: true,
    iconName: 'Clipboard',
  },
  {
    key: 'datacenter',
    label: '데이터센터',
    parentKey: null,
    defaultSortOrder: 2,
    defaultActive: true,
    iconName: 'Database',
  },

  // ===== status 하위 =====
  {
    key: 'status.my-work',
    label: '내 현황',
    parentKey: 'status',
    defaultSortOrder: 0,
    defaultActive: true,
    path: '/dashboard/mytask',
  },
  {
    key: 'status.overall',
    label: '누적 현황',
    parentKey: 'status',
    defaultSortOrder: 1,
    defaultActive: true,
    path: '/dashboard/overall',
  },
  {
    key: 'status.detail',
    label: '상세 현황',
    parentKey: 'status',
    defaultSortOrder: 2,
    defaultActive: true,
    path: '/dashboard/detail',
  },
  {
    key: 'status.performance',
    label: '성과지표 현황',
    parentKey: 'status',
    defaultSortOrder: 3,
    defaultActive: true,
    path: '/dashboard/performance',
  },
  {
    key: 'status.report-weekly',
    label: '주간 성과보고서',
    parentKey: 'status',
    defaultSortOrder: 4,
    defaultActive: true,
    path: '/dashboard/report/weekly',
  },
  {
    key: 'status.report-monthly',
    label: '월간 성과보고서',
    parentKey: 'status',
    defaultSortOrder: 5,
    defaultActive: true,
    path: '/dashboard/report/monthly',
  },
  {
    key: 'status.strategy',
    label: '누적 현황',
    parentKey: 'status',
    defaultSortOrder: 6,
    defaultActive: true,
    path: '/dashboard/strategy',
  },

  // ===== project 하위 =====
  {
    key: 'project.task',
    label: '과제/업무 관리',
    parentKey: 'project',
    defaultSortOrder: 0,
    defaultActive: true,
    path: '/project/task',
  },
  {
    key: 'project.performance',
    label: '성과지표 관리',
    parentKey: 'project',
    defaultSortOrder: 1,
    defaultActive: true,
    path: '/project/performance',
  },
  {
    key: 'project.report',
    label: '보고서 관리',
    parentKey: 'project',
    defaultSortOrder: 2,
    defaultActive: true,
    path: '/project/report',
  },
  {
    key: 'project.meeting',
    label: '회의록 관리',
    parentKey: 'project',
    defaultSortOrder: 3,
    defaultActive: true,
    path: '/project/meeting',
  },

  // ===== datacenter 하위 =====
  {
    key: 'datacenter.staff-workload',
    label: '직원업무보유현황',
    parentKey: 'datacenter',
    defaultSortOrder: 0,
    defaultActive: true,
    path: '/data/staff-workload',
    adminOnly: true,
  },
  {
    key: 'datacenter.change-history',
    label: '변경 이력',
    parentKey: 'datacenter',
    defaultSortOrder: 1,
    defaultActive: true,
    path: '/data/change-history',
    adminOnly: true,
  },
  {
    key: 'datacenter.university',
    label: '대학알리미',
    parentKey: 'datacenter',
    defaultSortOrder: 2,
    defaultActive: true,
    path: '/data/university',
  },
  {
    key: 'datacenter.export',
    label: '데이터 일괄 다운로드',
    parentKey: 'datacenter',
    defaultSortOrder: 3,
    defaultActive: true,
    path: '/data/export',
    adminOnly: true,
  },
]

/** menu_key → catalog entry 빠른 조회용 맵 */
export const MENU_CATALOG_MAP: Readonly<Record<string, MenuCatalogEntry>> =
  MENU_CATALOG.reduce<Record<string, MenuCatalogEntry>>((acc, entry) => {
    acc[entry.key] = entry
    return acc
  }, {})

/** 1depth만 추출 */
export function getParentEntries(): MenuCatalogEntry[] {
  return MENU_CATALOG.filter(e => e.parentKey === null)
}

/** 특정 parent의 children만 추출 */
export function getChildEntries(parentKey: ParentMenuKey): MenuCatalogEntry[] {
  return MENU_CATALOG.filter(e => e.parentKey === parentKey)
}
